import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import Spinner from '@cloudscape-design/components/spinner';
import Alert from '@cloudscape-design/components/alert';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useAuth } from '../context/AuthContext';

/**
 * OAuth callback handler for the Federate (Midway) sign-in redirect.
 *
 * Amplify intercepts the ?code= and ?state= query parameters automatically
 * when configured for hosted-UI OAuth, exchanges the code for tokens, and
 * fires a Hub `signedIn` event. The AuthContext listens for that event,
 * builds the user record, and we navigate to the dashboard.
 *
 * If Cognito redirected back with an error, the URL contains
 * ?error=...&error_description=... — we render those instead.
 */
export function CallbackPage(): React.ReactElement {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();

  const error = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  useEffect(() => {
    if (error) return;
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
    // If sign-in fails silently, drop the user on the login page after a brief wait.
    if (!isLoading && !isAuthenticated) {
      const t = window.setTimeout(() => navigate('/login', { replace: true }), 4000);
      return () => window.clearTimeout(t);
    }
  }, [error, isAuthenticated, isLoading, navigate]);

  if (error) {
    return (
      <Box padding="xxl" textAlign="center">
        <SpaceBetween size="m">
          <Alert type="error" header="Sign in failed">
            {errorDescription || error}
          </Alert>
          <Box>
            <a href="/login">Return to sign in</a>
          </Box>
        </SpaceBetween>
      </Box>
    );
  }

  return (
    <Box padding="xxl" textAlign="center">
      <SpaceBetween size="m">
        <Spinner size="large" />
        <Box>Completing sign in...</Box>
      </SpaceBetween>
    </Box>
  );
}

export default CallbackPage;
